import React from "react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, fullWidth = true, className = "", ...props }, ref) => {
    const widthClass = fullWidth ? "w-full" : "";
    const errorClass = error
      ? "border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 dark:border-zinc-700 focus:border-primary-500 focus:ring-primary-500";

    return (
      <div className={widthClass}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
            {label}
          </label>
        )}
        <input
          ref={ref}
          className={`
            ${widthClass} px-3 py-2 border rounded-md text-sm
            bg-white dark:bg-surface-dark-alt text-gray-900 dark:text-zinc-100
            focus:outline-none focus:ring-2 focus:ring-offset-0
            transition-colors duration-200
            disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-500 dark:disabled:text-zinc-500 disabled:cursor-not-allowed
            ${errorClass} ${className}
          `}
          {...props}
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, fullWidth = true, className = "", ...props }, ref) => {
    const widthClass = fullWidth ? "w-full" : "";
    const errorClass = error
      ? "border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 dark:border-zinc-700 focus:border-primary-500 focus:ring-primary-500";

    return (
      <div className={widthClass}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          className={`
            ${widthClass} px-3 py-2 border rounded-md text-sm resize-none
            bg-white dark:bg-surface-dark-alt text-gray-900 dark:text-zinc-100
            focus:outline-none focus:ring-2 focus:ring-offset-0
            transition-colors duration-200
            disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-500 dark:disabled:text-zinc-500 disabled:cursor-not-allowed
            ${errorClass} ${className}
          `}
          {...props}
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, fullWidth = true, className = "", children, ...props }, ref) => {
    const widthClass = fullWidth ? "w-full" : "";
    const errorClass = error
      ? "border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 dark:border-zinc-700 focus:border-primary-500 focus:ring-primary-500";

    return (
      <div className={widthClass}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
            {label}
          </label>
        )}
        <select
          ref={ref}
          className={`
            ${widthClass} px-3 py-2 border rounded-md text-sm
            bg-white dark:bg-surface-dark-alt text-gray-900 dark:text-zinc-100
            focus:outline-none focus:ring-2 focus:ring-offset-0
            transition-colors duration-200
            disabled:bg-gray-50 dark:disabled:bg-zinc-900 disabled:text-gray-500 dark:disabled:text-zinc-500 disabled:cursor-not-allowed
            ${errorClass} ${className}
          `}
          {...props}
        >
          {children}
        </select>
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";
